import { updateDesignByPrompt } from 'api/updateDesignByPrompt';
import { ChatData } from 'modules/common/components/Chat/Chat.types';
import { MessageType } from 'modules/common/components/Chat/Chat.types';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { TableData } from 'types/spreadsheet';
import { WidgetType } from 'types/widget.types';
import { ChartWidgetData, isHighChartsWidgetType } from 'widgets/ChartWidgets/ChartWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import widgetVersionManager from 'widgets/WidgetVersionManager';

import { DesignGenerationType, InfographicTemplateData } from '../DesignGenerator.types';
import { replaceCharts, replaceIcons, replaceImages } from '../helpers';
import { categoryExampleTopicsMap } from './DesignAI.config';
import { ChatCommand, CommandType } from './DesignAI.types';
import { DesignAICategories } from './DesignAIContext';

type ProcessDesignTemplateArgs = {
  // Template data taken from config
  baseTemplateData: InfographicTemplateData;

  // Infograph that was processed by backend
  generatedInfograph: InfographState;

  // Keywords obtained from OpenAi - used for icon/image search and replace
  keywords?: string[];

  // Table data used for chart generation. May update to use S3 in the future
  tableData?: TableData;
  tableMeta?: Partial<ChartWidgetData> & {
    chartType?: string;
  };
};

/**
 * Processes a generated infograph for card templates.
 * Processing has 3 steps:
 * 1. Replace icons - as defined by replaceables
 * 2. Apply a randomly selected colour palette
 * 3. Replace images (TODO)
 *
 * @param templateId base template id
 * @param generatedInfograph
 * @param baseColor
 * @returns updated infograph
 */
export const processDesignTemplate = async ({
  baseTemplateData,
  generatedInfograph,
  keywords,
  tableData,
  tableMeta = {},
}: ProcessDesignTemplateArgs): Promise<InfographState> => {
  const { replaceables } = baseTemplateData;

  // Step 1: Replace Icons
  let updatedInfograph = generatedInfograph;
  if (replaceables?.icons?.length && keywords?.length) {
    updatedInfograph = await replaceIcons(updatedInfograph, replaceables.icons, keywords);
  }

  if (replaceables?.images?.length && keywords?.length) {
    updatedInfograph = await replaceImages(updatedInfograph, replaceables.images, keywords);
  }

  if (replaceables?.charts?.length && !!tableData) {
    updatedInfograph = await replaceCharts(updatedInfograph, replaceables.charts, [tableData], [tableMeta]);
  }

  return updatedInfograph;
};

type GenerateDesignByAPIProps = {
  // The design id where the design will be generated from
  e2DesignId: WidgetId;

  // the user input on the input field
  userContext: string;

  // the generation type
  generationType: DesignGenerationType;

  // Uploaded csv data
  tableData?: TableData;

  // is the endpoint calling from the public website, which means non-logged in user
  isPublic?: boolean;
};

export const generateDesignByAPI = async ({
  e2DesignId,
  userContext,
  generationType,
  tableData,
  isPublic,
}: GenerateDesignByAPIProps): Promise<InfographState> => {
  const { status, updatedInfographState, searchKeywords, chartType } = await updateDesignByPrompt({
    designId: e2DesignId,
    prompt: userContext,
    type: generationType,
    tableData,
    isPublic,
  });

  if (status !== 'ok') {
    throw new Error('Failed to update design by prompt');
  }

  const updatedWidgetsMap = updatedInfographState.widgets;
  const widgetIds = Object.keys(updatedWidgetsMap) as WidgetId[];
  const iconWidgets = widgetIds.filter((id) => {
    return !updatedWidgetsMap[id].isLocked && getWidgetTypeFromId(id) === WidgetType.Icon;
  });
  const imageWidgets = widgetIds.filter(
    (id) => !updatedWidgetsMap[id].isLocked && getWidgetTypeFromId(id) === WidgetType.Image,
  );
  const chartWidgets = widgetIds.filter(
    (id) => !updatedWidgetsMap[id].isLocked && isHighChartsWidgetType(getWidgetTypeFromId(id)),
  );
  const replaceables = {
    icons: iconWidgets,
    images: imageWidgets,
    charts: chartWidgets,
  };

  const baseInfographTemplateData = {
    e2DesignId: e2DesignId,
    replaceables,
  };

  let processedInfographState = await processDesignTemplate({
    baseTemplateData: baseInfographTemplateData,
    generatedInfograph: updatedInfographState,
    keywords: searchKeywords,
    tableData,
    tableMeta: { chartType },
  });

  // Check widget order
  const widgetIdList = Object.keys(processedInfographState.widgets);
  const pageIdList = Object.keys(processedInfographState.pages);
  const invalidWidgetLayerOrder = processedInfographState.pages[pageIdList[0]].widgetLayerOrder.some(
    (id) => !widgetIdList.includes(id),
  );

  if (invalidWidgetLayerOrder) throw Error('Invalid widget layer order');

  // update the widgets to the latest version (the templates can have old versions of widgets)
  const updatedWidgets = widgetVersionManager.update(processedInfographState.widgets);
  processedInfographState.widgets = updatedWidgets;

  return processedInfographState;
};

/**
 * Get default chatData based on generation category.
 *
 * @param category generation category
 * @returns default chat data array
 */
export const getDefaultChatData = (category: DesignAICategories): ChatData[] => [
  {
    type: MessageType.BOT,
    messages: ['Give me a topic for your design or choose an example prompt below.'],
    createdAt: Date.now(),
  },
  {
    type: MessageType.SUGGESTION,
    messages: categoryExampleTopicsMap[category],
    createdAt: Date.now(),
  },
];

/**
 * Generate the CommandType object using the user input message
 * @param userMessage {string} user input message
 * @returns {ChatCommand | undefined}
 */
export const parseCommandToObject = (userMessage = ''): ChatCommand | undefined => {
  if (userMessage.startsWith(CommandType.DebugPanel)) {
    return {
      type: CommandType.DebugPanel,
      command: userMessage.replace(CommandType.DebugPanel, '').trim(),
    };
  }
};

/**
 * Remove empty cells and trim all cells in table data
 *
 * @param {TableData} tableData
 * @returns {string[][]}
 */
export const sanitizeTableData = (tableData: TableData): string[][] => {
  const result = tableData.map((row) => {
    return Array.from(row, (cell) => {
      return (cell ?? '').toString().trim();
    });
  });

  return result;
};
